import { CheckCircle2, Clock3, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import type { PersonelDetail } from '../model'
import { printDoc } from '../lib/exportActions'
import type { Personel } from '../types'
import { EgitimAtaModal } from './actions/EgitimAtaModal'
import { SectionCard } from './SectionCard'
import { HR_WRITE_PERM } from '../../permissions'

type Egitim = PersonelDetail['perf']['egitim'][number]

export function EgitimTimelineCard({ personel, egitim }: { personel: Personel; egitim: Egitim[] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [open, setOpen] = useState(false)

  const sertifika = (g: Egitim) =>
    printDoc({
      title: t('hr.personel.actions.egitim.sertifika'),
      subtitle: `${personel.ad} · ${personel.pozisyon}`,
      sections: [
        {
          rows: [
            [t('hr.personel.actions.egitim.ad'), g.b],
            [t('hr.personel.actions.egitim.saglayici'), g.a],
            [t('hr.personel.actions.egitim.tamamlandi'), g.t],
            [t('hr.personel.fields.sicil'), personel.sicil],
          ],
        },
      ],
    })

  return (
    <SectionCard
      title={t('hr.personel.perf.egitimTimeline')}
      action={
        can(HR_WRITE_PERM) ? (
          <Button size="sm" variant="outline" className="border-primary/40 text-primary" onClick={() => setOpen(true)}>
            <Plus />
            {t('hr.personel.perf.egitimAta')}
          </Button>
        ) : undefined
      }
    >
      {open ? <EgitimAtaModal personelId={personel.id} onClose={() => setOpen(false)} /> : null}
      <div className="space-y-3">
        {egitim.map((g) => (
          <div key={`${g.t}-${g.b}`} className="flex gap-3 rounded-xl border border-border/60 p-3">
            <div
              className={cn(
                'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
                g.state === 'done' ? 'bg-success/12 text-success' : 'bg-muted text-muted-foreground',
              )}
            >
              {g.state === 'done' ? <CheckCircle2 className="size-4" /> : <Clock3 className="size-4" />}
            </div>
            <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{g.b}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {g.a} · {g.t}
                </div>
              </div>
              {g.badge === 'Sertifika' ? (
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-info/30 px-2 py-0.5 text-[11px] font-medium text-info hover:bg-info/5"
                  onClick={() => sertifika(g)}
                >
                  {t('hr.personel.perf.sertifika')}
                </button>
              ) : (
                <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-foreground">
                  {t('hr.personel.perf.planlandi')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  )
}
